import { getOrSetRecord } from 'src/lib/getOrSetRecord';

export interface HistogramBucket {
  index: number;
  count: number;
}

// initialize bucket 0 with correct values
const BUCKET_RANGES: Record<
  number,
  { rangeBeginMs: number; rangeEndMs: number }
> = { 0: { rangeBeginMs: 0, rangeEndMs: 1 / 1000 } };
export function getBucketRange(index: number) {
  return getOrSetRecord(
    index,
    {
      rangeBeginMs: Math.pow(1.1, index - 1) / 1000,
      rangeEndMs: Math.pow(1.1, index) / 1000,
    },
    BUCKET_RANGES,
  );
}

export function getBucketAvg(index: number) {
  const bucket = getBucketRange(index);
  return (bucket.rangeBeginMs + bucket.rangeEndMs) / 2;
}

export function getPercentile(
  duration: number,
  histogramBuckets: ReadonlyArray<HistogramBucket>,
) {
  const v = histogramBuckets.reduce(
    (memo, b) => {
      const bucket = getBucketAvg(b.index);
      if (bucket < duration) {
        memo.countLess += b.count; // eslint-disable-line no-param-reassign
      }
      memo.countTotal += b.count; // eslint-disable-line no-param-reassign
      return memo;
    },
    {
      countLess: 0,
      countTotal: 0,
    },
  );
  return v.countLess / v.countTotal;
}

/*
    Given a percentile and buckets, we first sum the total request count distributed over the buckets.
    The bucket that contains the 99th percentile duration is the bucket that contains the percentile * count or the closest one.
  */
// percentile should be a decimal point number e.g: p50 = 0.5
export function getDurationFromPercentile(
  histogramBuckets: ReadonlyArray<HistogramBucket>,
  percentile: number,
) {
  const pBucket = getBucketFromPercentile(histogramBuckets, percentile);
  return pBucket ? getBucketAvg(pBucket.index) : null;
}

export function getBucketFromPercentile(
  histogramBuckets: ReadonlyArray<HistogramBucket>,
  percentile: number,
) {
  let pBucket;
  if (percentile === 0) {
    pBucket = histogramBuckets.find((b) => b.count > 0);
  } else {
    const total = histogramBuckets.reduce((memo, b) => memo + b.count, 0);
    const target = percentile * total;
    let runningSum = 0;
    pBucket = histogramBuckets.find((b) => {
      runningSum += b.count;
      return runningSum >= target;
    });
  }

  return pBucket;
}

export function getBucketIndex(duration: number) {
  return Math.max(
    0,
    Math.min(Math.ceil(Math.log(duration * 1000) / Math.log(1.1)), 255),
  );
}
